import * as React from 'react';
import { EditorState, RichUtils, AtomicBlockUtils, Modifier, ContentState, DraftEditorCommand, DraftBlockType } from 'draft-js';
import { Grid } from '@material-ui/core';
import { Paper } from '@material-ui/core';
import Editor, { composeDecorators } from 'draft-js-plugins-editor';
import createImagePlugin from 'draft-js-image-plugin';
import createAlignmentPlugin from 'draft-js-alignment-plugin';
import createFocusPlugin from 'draft-js-focus-plugin';
import createResizeablePlugin from './plugins/resizable-plugin';
import createBlockDndPlugin from 'draft-js-drag-n-drop-plugin';
import Toolbar from './Toolbar/Toolbar';
import createHighlightPlugin from './plugins/highlightPlugin'
import createLinkPlugin from './plugins/addLinkPlugin';
import textColorPlugin from './plugins/textColorPlugin';
import { colorStyleMap } from './Toolbar/ColorControls';
import { Color } from 'csstype';
import { ExtendedRichUtils, ALIGNMENT_DATA_KEY, ALIGNMENTS } from './utility/extendedRichUtils';

require('./editorStyles.scss');
require('draft-js-image-plugin/lib/plugin.css');
require('draft-js-focus-plugin/lib/plugin.css');
require('draft-js-alignment-plugin/lib/plugin.css');

const linkPlugin = createLinkPlugin;
const highlightPlugin = createHighlightPlugin();
const focusPlugin = createFocusPlugin();
const resizeablePlugin = createResizeablePlugin({});
const blockDndPlugin = createBlockDndPlugin();
const alignmentPlugin = createAlignmentPlugin();
const { AlignmentTool } = alignmentPlugin;

const decorator = composeDecorators(
    resizeablePlugin.decorator,
    focusPlugin.decorator,
    alignmentPlugin.decorator,
    blockDndPlugin.decorator
);

const imagePlugin = createImagePlugin({ decorator });

const getBlockStyle = (block: any) => {
    const textAlignStyle = block.getData().get(ALIGNMENT_DATA_KEY);
    switch (textAlignStyle) {
        case ALIGNMENTS.RIGHT:
            return `align-right`;
        case ALIGNMENTS.CENTER:
            return `align-center`;
        case ALIGNMENTS.LEFT:
            return `align-left`;
        case ALIGNMENTS.JUSTIFY:
            return `align-justify`;
        default:
            return null;
    }
}

export interface IRichTextEditorProps {
    initialState?: EditorState,
    placeHolderText: string,
    id?: string,
    onChange?: (editorState: EditorState) => void,
    readOnly: boolean,
}

interface IRichTextEditorState {
    editorState: EditorState,
    id?: string,
    currentColor?: Color
}

const UndefinedColor = (): Color => undefined;

export class RichTextEditor extends React.Component<IRichTextEditorProps, IRichTextEditorState> {
    plugins: Array<any>;
    //editorRef = useRef(null);
    editorRef: any = null;
    //getEditorRef: () => any ;
    constructor(props: IRichTextEditorProps) {
        super(props);
        this.state = {
            editorState: this.props.initialState ? this.props.initialState : EditorState.createWithContent(ContentState.createFromText(this.props.placeHolderText)),
            id: this.props.id ? this.props.id : undefined,
            currentColor: UndefinedColor(),
        }

        this.plugins = [
            blockDndPlugin,
            focusPlugin,
            alignmentPlugin,
            resizeablePlugin,
            imagePlugin,
            linkPlugin,
            highlightPlugin,
            textColorPlugin,
        ];
    }


    static getDerivedStateFromProps(nextProps: IRichTextEditorProps, prevState: IRichTextEditorState) {
        if (nextProps && nextProps.id !== prevState.id) {
            return {
                editorState: nextProps.initialState ? nextProps.initialState : EditorState.createWithContent(ContentState.createFromText(nextProps.placeHolderText)),
                id: nextProps.id,
                currentColor: UndefinedColor()
            }
        }
        else return null;
    }


    componentDidMount() {
        this.focus();
    }
    focus = (): void => {
        if (this.editorRef)
            this.editorRef.focus();
    }
    onChange = (editorState: EditorState) => {
        if (editorState.getDecorator() !== null) {
            this.setState({
                editorState: editorState
            });
            if (this.props.onChange)
                this.props.onChange(editorState);
        }
    }
    handleKeyCommand = (command: DraftEditorCommand) => {
        //debugger
        const newState = RichUtils.handleKeyCommand(this.state.editorState, command)
        if (newState) {
            this.onChange(newState);
            this.focus();
            return 'handled';
        }
        return 'not-handled';
    }
    onUnderlineClick = () => {
        console.log('underlining');
        this.onChange(RichUtils.toggleInlineStyle(this.state.editorState, 'UNDERLINE'));
        this.focus();
    }
    onAlign = (align: ALIGNMENTS) => {
        console.log('aligning');
        const { editorState } = this.state;
        this.onChange(ExtendedRichUtils.toggleAlignment(editorState, align));
        this.focus();
    }
    onBoldClick = () => {
        console.log('bolding');
        this.onChange(RichUtils.toggleInlineStyle(this.state.editorState, 'BOLD'))
        this.focus();
    }

    onItalicClick = () => {
        console.log('italic-ing');
        this.onChange(RichUtils.toggleInlineStyle(this.state.editorState, 'ITALIC'))
        this.focus();
    }
    onHighlight = () => {
        console.log('highlighting');
        this.onChange(RichUtils.toggleInlineStyle(this.state.editorState, 'HIGHLIGHT'))
        this.focus();
    }
    onStrikeThroughClick = () => {
        console.log('linethrough-ing');
        this.onChange(
            RichUtils.toggleInlineStyle(this.state.editorState, "STRIKETHROUGH")
        );
        this.focus();
    };
    onAddLink = () => {
        console.log('addlink-ing');
        const editorState = this.state.editorState;
        const selection = editorState.getSelection();
        let link = window.prompt("Inserisci l'indirizzo web del collegamento")
        if (!link) {
            this.onChange(RichUtils.toggleLink(editorState, selection, null));
            return 'handled';
        }
        else {
            if (!link.toLowerCase().startsWith('http'))
                link = 'https://' + link;

            const content = editorState.getCurrentContent();
            const contentWithEntity = content.createEntity('LINK', 'MUTABLE', { url: link });
            const newEditorState = EditorState.push(editorState, contentWithEntity, 'apply-entity');
            const entityKey = contentWithEntity.getLastCreatedEntityKey();
            this.onChange(RichUtils.toggleLink(newEditorState, selection, entityKey))
        }
    };
    toggleBlockType = (blockType: DraftBlockType) => {
        console.log('toggleing ' + blockType);
        this.onChange(RichUtils.toggleBlockType(this.state.editorState, blockType));
        this.focus();
    };

    onAddImage = (canvas: any) => {
        console.log('adding image');
        
        var newDataUri = canvas.toDataURL();

        const editorState = this.state.editorState;
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(
            "image",
            "IMMUTABLE",
            { src: newDataUri }
        );
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const newEditorState = EditorState.push(
            editorState,
            contentStateWithEntity,
            "apply-entity"
        );
        const newState = AtomicBlockUtils.insertAtomicBlock(
            newEditorState,
            entityKey,
            " "
        );
        this.setState({
            editorState: newState
        }
        );
        this.onChange(newState);
        this.focus();
    }
    imageToDataUri = (img) => {
        //const img = e.currentTarget;
        // create an off-screen canvas
        var canvas = document.createElement('canvas'),
            ctx = canvas.getContext('2d');

        const max_size = 1200;// TODO : max size for a pic  
        let width = img.width;
        let height = img.height;
        if (width > max_size) {
            height *= max_size / width;
            width = max_size;
        }
        if (height > max_size) {
            width *= max_size / height;
            height = max_size;
        }

        // set its dimension to target size
        canvas.width = width;
        canvas.height = height;

        // draw source image into the off-screen canvas:
        ctx.drawImage(img, 0, 0, width, height);

        // encode image to data-uri with base64 version of compressed image
        return canvas.toDataURL();
    }
    unselectAll(): any {

    };

    onToggleCode = () => {
        this.onChange(RichUtils.toggleCode(this.state.editorState));
        console.log('toggle-code-block');
    };

    toggleColor = (toggledColor: string) => {
        const { editorState } = this.state;
        const selection = editorState.getSelection();
        // Let's just allow one color at a time. Turn off all active colors.
        if (!colorStyleMap[toggledColor])
            colorStyleMap[toggledColor] = { color: toggledColor };
        const nextContentState = Object.keys(colorStyleMap)
            .reduce((contentState, color) => {
                return Modifier.removeInlineStyle(contentState, selection, color)
            }, editorState.getCurrentContent());
        let nextEditorState = EditorState.push(
            editorState,
            nextContentState,
            'change-inline-style'
        );
        //orderset<stribng>
        const currentStyle = editorState.getCurrentInlineStyle();
        // Unset style override for current color.
        if (selection.isCollapsed()) {
            nextEditorState = currentStyle.reduce((state, color) => {
                if (state && color)
                    return RichUtils.toggleInlineStyle(state, color);
                return nextEditorState;
            }, nextEditorState);
        }
        // If the color is being toggled on, apply it.
        if (!currentStyle.has(toggledColor)) {
            nextEditorState = RichUtils.toggleInlineStyle(
                nextEditorState,
                toggledColor
            );
        }
        this.onChange(nextEditorState);
    }


    render() {
        const { readOnly } = this.props;
        return (
            <React.Fragment>
                {!readOnly && <Toolbar onSelectColor={c => this.toggleColor(c)}
                    selectedColor={this.state.currentColor}
                    editorState={this.state.editorState}
                    onAddImage={this.onAddImage}
                    onAddLink={this.onAddLink}
                    onBoldClick={this.onBoldClick}
                    onHighlight={this.onHighlight}
                    onItalicClick={this.onItalicClick}
                    onStrikeThroughClick={this.onStrikeThroughClick}
                    onUnderlineClick={this.onUnderlineClick}
                    toggleBlockType={this.toggleBlockType}
                    onAlign={this.onAlign}
                    onToggleCode={this.onToggleCode}
                />}
                <div className="editor">
                    <Grid item container xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Grid item sm={12}>
                            <Paper>
                                <Editor
                                    ref={el => { this.editorRef = el }}
                                    editorState={this.state.editorState}
                                    //handleKeyCommand={this.handleKeyCommand}
                                    onChange={this.onChange}
                                    plugins={this.plugins}
                                    blockStyleFn={getBlockStyle}
                                    customStyleMap={colorStyleMap}
                                    readOnly={readOnly}
                                />
                                <AlignmentTool />
                            </Paper>
                        </Grid>
                    </Grid>
                </div>
            </React.Fragment>
        );
    }
}